/**
 * Template Builder Page — machine-aware section builder with part grouping.
 *
 * Supports: optional machine assignment, general sections, part-specific sections,
 * auto-save drafts, publish validation, and version history sidebar.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TemplatePreviewDialog from './TemplatePreviewDialog';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import BuildIcon from '@mui/icons-material/Build';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SidebarTracker from './SidebarTracker';

import { ChecklistTemplateControllerApi, MachineControllerApi, MachineChecklistCategoryControllerApi, SafetyWarningControllerApi } from '../../api-generated/apis';
import type { ChecklistTemplate, Machine, MachinePart, MachineChecklistCategory } from "../../api-generated";
import { apiConfig } from '../../api-generated/apiConfig';
import type { SectionType } from '../../types';

const templateApi = new ChecklistTemplateControllerApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);
const categoryApi = new MachineChecklistCategoryControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

interface SectionItem {
  id?: string;
  machinePartId?: string;
  machinePartName?: string;
  question: string;
  sectionType: SectionType;
  index: number;
  minValue?: number;
  maxValue?: number;
  warningMin?: number;
  warningMax?: number;
}

interface MachineOption {
  id: string;
  name: string;
}

interface PartOption {
  id: string;
  name: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SECTION_TYPE_OPTIONS: { value: SectionType; label: string }[] = [
  { value: 'STATUS_CHECK', label: 'Condition Check' },
  { value: 'NUMBER_ENTRY', label: 'Number Entry' },
  { value: 'TEXT_RESULT', label: 'Text Result' },
];

const AUTO_SAVE_INTERVAL_MS = 30_000;

// ─── Component ───────────────────────────────────────────────────────────────

const TemplateBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ─── State ──────────────────────────────────────────────────────────────
  const [templateName, setTemplateName] = useState('Untitled Template');
  const [description, setDescription] = useState('');
  const [machineId, setMachineId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [generalWarningIds, setGeneralWarningIds] = useState<string[]>([]);
  const [sections, setSections] = useState<SectionItem[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Refs for auto-save
  const isDirtyRef = useRef(isDirty);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  // ─── Queries ────────────────────────────────────────────────────────────

  const { data: templateData, isLoading } = useQuery<ChecklistTemplate>({
    queryKey: ['templates', 'detail', id],
    queryFn: () => templateApi.getTemplate({ id: id! }),
    enabled: !!id && id !== 'new',
  });

  const { data: machinesData } = useQuery<Machine[]>({
    queryKey: ['machines-list'],
    queryFn: () => machineApi.getAllMachines(),
    staleTime: 60_000,
  });

  const { data: categoriesData } = useQuery<MachineChecklistCategory[]>({
    queryKey: ['categories-list'],
    queryFn: () => categoryApi.getAllCategories(),
    staleTime: 60_000,
  });

  const { data: warningsData } = useQuery<any[]>({
    queryKey: ['safety-warnings-list'],
    queryFn: async () => {
      const warningApi = new SafetyWarningControllerApi(apiConfig);
      return warningApi.getAll1();
    },
    staleTime: 60_000,
  });

  const { data: versionsData } = useQuery<ChecklistTemplate[]>({
    queryKey: ['templates', id, 'versions'],
    queryFn: () => templateApi.getAllVersions({ id: id! }),
    enabled: !!id && id !== 'new',
  });

  const { data: partsData } = useQuery<MachinePart[]>({
    queryKey: ['machine-parts', machineId],
    queryFn: () => machineApi.getAllMachinePartsLegacy({ id: machineId! }),
    enabled: !!machineId,
  });

  const machineOptions: MachineOption[] = useMemo(
    () => (machinesData ?? []).map((m: any) => ({ id: m.id ?? '', name: m.name ?? '' })),
    [machinesData],
  );

  const categoryOptions = useMemo(
    () => (categoriesData ?? []).map((c: any) => ({ id: c.id ?? '', name: c.name ?? '' })),
    [categoriesData],
  );

  const warningOptions = useMemo(
    () => (warningsData ?? []).map((w: any) => ({ id: w.id ?? '', title: w.title ?? '', severity: w.severity ?? '' })),
    [warningsData],
  );

  const partOptions: PartOption[] = useMemo(
    () => (partsData ?? []).map((p: any) => ({ id: p.id ?? '', name: p.name ?? '' })),
    [partsData],
  );

  // ─── Seed state from fetched template ───────────────────────────────────

  useEffect(() => {
    if (templateData) {
      setTemplateName(templateData.name ?? '');
      setDescription(templateData.description ?? '');
      setMachineId((templateData as any).machineId ?? null);
      setCategoryId((templateData as any).category?.id ?? null);
      setGeneralWarningIds((templateData as any).generalWarningIds ?? []);

      const allItems: SectionItem[] = [];
      (templateData.sections ?? []).forEach((section: any) => {
        (section.items ?? []).forEach((item: any) => {
          allItems.push({
            id: item.id,
            machinePartId: item.machinePartId ?? section.machinePartId ?? undefined,
            machinePartName: item.machinePartName ?? section.machinePartName ?? undefined,
            question: item.question ?? section.name ?? '',
            sectionType: item.type ?? 'STATUS_CHECK',
            index: item.orderIndex ?? allItems.length,
            minValue: item.minValue,
            maxValue: item.maxValue,
            warningMin: item.warningMin,
            warningMax: item.warningMax,
          });
        });
      });
      setSections(allItems.sort((a, b) => a.index - b.index));
    }
  }, [templateData]);

  // ─── Mutations ──────────────────────────────────────────────────────────

  const buildPayload = () => {
    const generalSections = sections.filter(s => !s.machinePartId);
    const partSections = sections.filter(s => !!s.machinePartId);

    const partGroups = partSections.reduce<Record<string, SectionItem[]>>((acc, s) => {
      const key = s.machinePartId!;
      if (!acc[key]) acc[key] = [];
      acc[key].push(s);
      return acc;
    }, {});

    const apiSections: any[] = [];

    // General sections as one section group
    if (generalSections.length > 0) {
      apiSections.push({
        name: 'General',
        orderIndex: 0,
        items: generalSections.map((s, i) => ({
          question: s.question,
          type: s.sectionType,
          orderIndex: i,
          required: true,
          minValue: s.minValue,
          maxValue: s.maxValue,
          warningMin: s.warningMin,
          warningMax: s.warningMax,
        })),
      });
    }

    // Part-specific sections
    Object.entries(partGroups).forEach(([partId, items], groupIdx) => {
      const partName = items[0]?.machinePartName ?? partOptions.find(p => p.id === partId)?.name ?? partId;
      apiSections.push({
        name: partName,
        machinePartId: partId,
        machinePartName: partName,
        orderIndex: groupIdx + 1,
        items: items.map((s, i) => ({
          question: s.question,
          type: s.sectionType,
          orderIndex: i,
          required: true,
          machinePartId: partId,
          machinePartName: partName,
          minValue: s.minValue,
          maxValue: s.maxValue,
          warningMin: s.warningMin,
          warningMax: s.warningMax,
        })),
      });
    });

    const selectedCategory = categoryOptions.find((c: any) => c.id === categoryId);
    return {
      name: templateName,
      description,
      machineId: machineId || undefined,
      generalWarningIds: generalWarningIds.length > 0 ? generalWarningIds : undefined,
      category: selectedCategory ? { id: selectedCategory.id, name: selectedCategory.name } : undefined,
      sections: apiSections,
    };
  };

  const createMutation = useMutation({
    mutationFn: () => templateApi.createTemplate({ checklistTemplate: buildPayload() as any }),
    onMutate: () => setSaveStatus('saving'),
    onSuccess: (result) => {
      setSaveStatus('saved');
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      if (result.id) navigate(`/templates/${result.id}/edit`, { replace: true });
    },
    onError: () => setSaveStatus('idle'),
  });

  const updateMutation = useMutation({
    mutationFn: () => templateApi.updateTemplate({ id: id!, checklistTemplate: buildPayload() as any }),
    onMutate: () => setSaveStatus('saving'),
    onSuccess: () => {
      setSaveStatus('saved');
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['templates', 'detail', id] });
    },
    onError: () => setSaveStatus('idle'),
  });

  const publishMutation = useMutation({
    mutationFn: () => templateApi.publishTemplate({ id: id! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templates', id, 'versions'] });
      queryClient.invalidateQueries({ queryKey: ['templates', 'detail', id] });
      setErrors([]);
    },
  });

  // ─── Auto-save ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!id || id === 'new') return;
    const timer = setInterval(() => {
      if (isDirtyRef.current) updateMutation.mutate();
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (saveStatus === 'saved') {
      const t = setTimeout(() => setSaveStatus('idle'), 3000);
      return () => clearTimeout(t);
    }
  }, [saveStatus]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const markDirty = useCallback(() => { setIsDirty(true); setSaveStatus('idle'); }, []);

  const handleSave = useCallback(() => {
    if (!id || id === 'new') {
      createMutation.mutate();
    } else {
      updateMutation.mutate();
    }
  }, [id, createMutation, updateMutation]);

  const handlePublish = useCallback(() => {
    if (!id || id === 'new') {
      setErrors(['Please save the template before publishing.']);
      return;
    }
    if (sections.length === 0) {
      setErrors(['Template must have at least one section.']);
      return;
    }
    const empty = sections.filter(s => !s.question.trim());
    if (empty.length > 0) {
      setErrors(['All sections must have a question.']);
      return;
    }
    setErrors([]);
    publishMutation.mutate();
  }, [id, sections, publishMutation]);

  const handleAddSection = useCallback((partId?: string, partName?: string) => {
    setSections(prev => [...prev, {
      question: '',
      sectionType: 'STATUS_CHECK',
      index: prev.length,
      machinePartId: partId,
      machinePartName: partName,
    }]);
    markDirty();
  }, [markDirty]);

  const handleDeleteSection = useCallback((index: number) => {
    setSections(prev => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, index: i })));
    markDirty();
  }, [markDirty]);

  const handleSectionChange = useCallback((index: number, field: keyof SectionItem, value: unknown) => {
    setSections(prev => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
    markDirty();
  }, [markDirty]);

  // ─── Grouped sections ──────────────────────────────────────────────────

  const generalSections = sections.filter(s => !s.machinePartId);
  const partGrouped = useMemo(() => {
    const groups: Record<string, { name: string; items: SectionItem[] }> = {};
    sections.forEach(s => {
      if (s.machinePartId) {
        if (!groups[s.machinePartId]) {
          groups[s.machinePartId] = { name: s.machinePartName ?? s.machinePartId, items: [] };
        }
        groups[s.machinePartId].items.push(s);
      }
    });
    return groups;
  }, [sections]);

  // ─── Render helpers ────────────────────────────────────────────────────

  const renderSectionCard = (section: SectionItem, globalIndex: number) => (
    <Paper id={`section-card-${globalIndex}`} key={globalIndex} variant="outlined" sx={{ mb: 1.5, p: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1 }}>
          <TextField
            label="Question"
            value={section.question}
            onChange={(e) => handleSectionChange(globalIndex, 'question', e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Type"
            select
            value={section.sectionType}
            onChange={(e) => handleSectionChange(globalIndex, 'sectionType', e.target.value)}
            size="small"
            sx={{ minWidth: 180, mb: section.sectionType === 'NUMBER_ENTRY' ? 1.5 : 0 }}
          >
            {SECTION_TYPE_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          {section.sectionType === 'NUMBER_ENTRY' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
              <TextField label="Min" type="number" size="small" value={section.minValue ?? ''}
                onChange={(e) => handleSectionChange(globalIndex, 'minValue', e.target.value === '' ? undefined : Number(e.target.value))} />
              <TextField label="Max" type="number" size="small" value={section.maxValue ?? ''}
                onChange={(e) => handleSectionChange(globalIndex, 'maxValue', e.target.value === '' ? undefined : Number(e.target.value))} />
              <TextField label="Warning Min" type="number" size="small" value={section.warningMin ?? ''}
                onChange={(e) => handleSectionChange(globalIndex, 'warningMin', e.target.value === '' ? undefined : Number(e.target.value))} />
              <TextField label="Warning Max" type="number" size="small" value={section.warningMax ?? ''}
                onChange={(e) => handleSectionChange(globalIndex, 'warningMax', e.target.value === '' ? undefined : Number(e.target.value))} />
            </Box>
          )}
        </Box>
        <IconButton size="small" color="error" onClick={() => handleDeleteSection(globalIndex)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );

  // ─── Loading ───────────────────────────────────────────────────────────

  if (isLoading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Loading...</Typography></Box>;
  }

  // ─── Sidebar data ──────────────────────────────────────────────────────

  const versions = (versionsData ?? []).map((v: any) => ({
    id: v.id,
    version: v.version ?? 0,
    status: v.status ?? 'DRAFT',
    createdAt: v.createdAt?.toISOString?.() ?? v.createdAt ?? '',
  }));

  const handleScrollToSection = (index: number) => {
    const el = document.getElementById(`section-card-${index}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', gap: 2, py: 2 }}>
    {/* Main content */}
    <Box sx={{ flex: 1, maxWidth: 900 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <TextField
          value={templateName}
          onChange={(e) => { setTemplateName(e.target.value); markDirty(); }}
          variant="standard"
          placeholder="Template Name"
          InputProps={{ disableUnderline: true, sx: { fontSize: '1.5rem', fontWeight: 600 } }}
          sx={{ flex: 1, mr: 2 }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          {saveStatus === 'saving' && <Typography variant="body2" color="text.secondary">Saving...</Typography>}
          {saveStatus === 'saved' && <Typography variant="body2" color="success.main">Saved</Typography>}
          <Button variant="text" size="small" startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)} disabled={sections.length === 0}>
            Preview
          </Button>
          <Button variant="outlined" size="small" onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending}>
            Save Draft
          </Button>
          <Button variant="contained" size="small" onClick={handlePublish}
            disabled={publishMutation.isPending}>
            Publish
          </Button>
        </Stack>
      </Stack>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.join('. ')}</Alert>
      )}
      {publishMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>Published successfully.</Alert>
      )}

      {/* Category & Machine selector */}
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Category (optional)
            </Typography>
            <Autocomplete
              options={categoryOptions}
              getOptionLabel={(opt) => opt.name}
              value={categoryOptions.find((c: any) => c.id === categoryId) ?? null}
              onChange={(_, val) => { setCategoryId(val?.id ?? null); markDirty(); }}
              renderInput={(params) => <TextField {...params} placeholder="Select category..." size="small" />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Assign to Machine (optional)
            </Typography>
            <Autocomplete
              options={machineOptions}
              getOptionLabel={(opt) => opt.name}
              value={machineOptions.find(m => m.id === machineId) ?? null}
              onChange={(_, val) => { setMachineId(val?.id ?? null); markDirty(); }}
              renderInput={(params) => <TextField {...params} placeholder="Select machine..." size="small" />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
            {machineId && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                You can add part-specific sections below for this machine&apos;s parts.
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              General Safety Warnings (optional)
            </Typography>
            <Autocomplete
              multiple
              options={warningOptions}
              getOptionLabel={(opt: any) => opt.title}
              value={warningOptions.filter((w: any) => generalWarningIds.includes(w.id))}
              onChange={(_, val) => { setGeneralWarningIds(val.map((v: any) => v.id)); markDirty(); }}
              renderInput={(params) => <TextField {...params} placeholder="Select warnings..." size="small" />}
              isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
            />
          </Box>
        </Stack>
      </Card>

      <Divider sx={{ mb: 3 }} />

      {/* General Sections */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="h6">General Sections</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddSection()}>
            Add
          </Button>
        </Stack>
        {generalSections.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No general sections yet.
          </Typography>
        )}
        {generalSections.map((section) => {
          const globalIndex = sections.indexOf(section);
          return renderSectionCard(section, globalIndex);
        })}
      </Box>

      {/* Part-specific Sections */}
      {machineId && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>Part Sections</Typography>

          {/* Existing part groups */}
          {Object.entries(partGrouped).map(([partId, group]) => (
            <Box key={partId} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Chip label={group.name} color="primary" variant="outlined" size="small" />
                <Button size="small" startIcon={<AddIcon />}
                  onClick={() => handleAddSection(partId, group.name)}>
                  Add
                </Button>
              </Stack>
              {group.items.map((section) => {
                const globalIndex = sections.indexOf(section);
                return renderSectionCard(section, globalIndex);
              })}
            </Box>
          ))}

          {/* Add section for a new part */}
          {partOptions.filter(p => !partGrouped[p.id]).length > 0 && (
            <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Add sections for another part:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {partOptions.filter(p => !partGrouped[p.id]).map(part => (
                  <Button key={part.id} size="small" variant="outlined"
                    onClick={() => handleAddSection(part.id, part.name)}>
                    + {part.name}
                  </Button>
                ))}
              </Stack>
            </Card>
          )}
        </>
      )}

    </Box>

    {/* Sidebar tracker */}
    <SidebarTracker
      sections={sections.map((s, idx) => ({
        index: idx,
        question: s.question,
        partName: s.machinePartName,
      }))}
      versions={versions}
      onSectionClick={handleScrollToSection}
    />

      {/* Preview Dialog */}
      <TemplatePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        templateName={templateName}
        machineName={machineOptions.find(m => m.id === machineId)?.name}
        sections={sections.map(s => ({
          question: s.question,
          sectionType: s.sectionType,
          machinePartName: s.machinePartName,
          minValue: s.minValue,
          maxValue: s.maxValue,
          warningMin: s.warningMin,
          warningMax: s.warningMax,
        }))}
      />

      {/* Floating Add Button */}
      <SpeedDial
        ariaLabel="Add section"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        icon={<SpeedDialIcon />}
      >
        <SpeedDialAction
          key="general"
          icon={<ListAltIcon />}
          tooltipTitle="General Section"
          onClick={() => {
            handleAddSection();
            setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
          }}
        />
        {machineId && partOptions.map(part => (
          <SpeedDialAction
            key={part.id}
            icon={<BuildIcon />}
            tooltipTitle={part.name}
            onClick={() => {
              handleAddSection(part.id, part.name);
              setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
            }}
          />
        ))}
      </SpeedDial>
    </Box>
  );
};

export default TemplateBuilderPage;
